import { useEffect, useState } from "react";

const PREVIEW_DEBOUNCE_MS = 600;

/**
 * Debounced-fetch state machine shared by every wizard's live "what will
 * actually get sent" document preview (Proposals, Invoices, Upsells/
 * Downsells) - each renders the org's own published document template
 * against the wizard's current, unsaved draft through the exact same
 * backend render pipeline the real send uses (see each entity's own
 * `previewXDraft` controller), so the preview can't drift from what a
 * customer actually receives the way a hand-built preview component could.
 * Extracted from _features/proposals/components/ProposalPreviewPane.jsx,
 * where this first shipped as inline state - see that file's git history.
 *
 * Debounced on a JSON snapshot of the draft payload (not a plain timer) so
 * unrelated re-renders inside the debounce window don't reset the wait, and
 * an in-flight request whose inputs changed before it resolved is ignored
 * rather than clobbering a newer one (`cancelled` flag, same pattern
 * useTemplatePreview.js already uses for the Template Editor's own preview).
 *
 * `canPreview`/`draftPayload` are split (rather than one nullable payload)
 * so a caller's own required-field guard (e.g. "needs company+market") stays
 * readable at the call site instead of encoded as `payload === null`.
 */
export const useDraftDocumentPreview = ({
  canPreview,
  draftPayload,
  previewMutation,
  notConfiguredMessage = "No published document template is configured for this organization yet.",
  errorMessage = "Couldn't render the preview. Try again shortly.",
}) => {
  const [html, setHtml] = useState("");
  // idle (nothing selected yet) | loading (no html yet) | refreshing (html
  // present, re-rendering after an edit) | ready | not-configured | error
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const snapshot = canPreview && draftPayload ? JSON.stringify(draftPayload) : null;

  useEffect(() => {
    if (!snapshot) {
      setStatus("idle");
      setHtml("");
      return undefined;
    }

    let cancelled = false;
    setStatus((prev) => (prev !== "idle" && html ? "refreshing" : "loading"));

    const timer = setTimeout(async () => {
      try {
        const response = await previewMutation(JSON.parse(snapshot)).unwrap();
        if (cancelled) return;
        setHtml(response?.html || "");
        setStatus("ready");
      } catch (error) {
        if (cancelled) return;
        if (error?.data?.code === "TEMPLATE_NOT_FOUND") {
          setStatus("not-configured");
          setMessage(error?.data?.message || notConfiguredMessage);
        } else {
          setStatus("error");
          setMessage(error?.data?.message || errorMessage);
        }
      }
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, previewMutation]);

  return { html, status, message };
};
